import {
  MAX_RECORDS_PER_SUBMIT,
  MAX_RESPONSES_PER_SUBMIT,
} from "@bindery/convex-reader/submission";
import type { OutboxRecordRow, OutboxResponseRow } from "@/storage/db";

export interface Chunk {
  records: OutboxRecordRow[];
  responses: OutboxResponseRow[];
}

export interface ChunkCaps {
  records: number;
  responses: number;
}

export const SUBMIT_CAPS: ChunkCaps = {
  records: MAX_RECORDS_PER_SUBMIT,
  responses: MAX_RESPONSES_PER_SUBMIT,
};

type Group = Chunk & { queuedAt: number };

// A tap's record and its Identification response travel together: the
// server computes actionIdDivergences only from a response in the same call.
function groupByTap(chunk: Chunk): Group[] {
  const groups = new Map<string, Group>();
  for (const record of chunk.records) {
    groups.set(record.clientRecordId, {
      queuedAt: record.queuedAt,
      records: [record],
      responses: [],
    });
  }
  for (const response of chunk.responses) {
    const tap =
      response.clientRecordId === undefined
        ? undefined
        : groups.get(response.clientRecordId);
    if (tap) {
      tap.responses.push(response);
      tap.queuedAt = Math.min(tap.queuedAt, response.queuedAt);
    } else {
      groups.set(`response:${response.clientResponseId}`, {
        queuedAt: response.queuedAt,
        records: [],
        responses: [response],
      });
    }
  }
  return [...groups.values()].sort((a, b) => a.queuedAt - b.queuedAt);
}

const fits = (chunk: Chunk, group: Group, caps: ChunkCaps): boolean =>
  chunk.records.length + group.records.length <= caps.records &&
  chunk.responses.length + group.responses.length <= caps.responses;

function pack(groups: readonly Group[], caps: ChunkCaps): Chunk[] {
  const chunks: Chunk[] = [];
  let current: Chunk = { records: [], responses: [] };
  for (const group of groups) {
    const empty =
      current.records.length === 0 && current.responses.length === 0;
    if (!empty && !fits(current, group, caps)) {
      chunks.push(current);
      current = { records: [], responses: [] };
    }
    current.records.push(...group.records);
    current.responses.push(...group.responses);
  }
  if (current.records.length > 0 || current.responses.length > 0) {
    chunks.push(current);
  }
  return chunks.map(inQueueOrder);
}

// learning.submit applies responses sorted by occurredAt, ties in array
// order, and one tap's responses share an occurredAt: grouping moved each
// Identification next to its record, so the queue order is restored here.
// Otherwise a word passed over and then tapped in one tap would be stored
// Identification first.
function inQueueOrder(chunk: Chunk): Chunk {
  return {
    records: chunk.records,
    responses: [...chunk.responses].sort((a, b) => a.queuedAt - b.queuedAt),
  };
}

/** Oldest tap first, each chunk within the server's per-call caps. */
export function chunkForSubmit(
  pending: Chunk,
  caps: ChunkCaps = SUBMIT_CAPS,
): Chunk[] {
  return pack(groupByTap(pending), caps);
}

/** Splits between taps, never inside one. */
export function halve(chunk: Chunk): Chunk[] {
  const groups = groupByTap(chunk);
  const middle = Math.ceil(groups.length / 2);
  return [groups.slice(0, middle), groups.slice(middle)]
    .filter((half) => half.length > 0)
    .map((half) => pack(half, { records: Infinity, responses: Infinity })[0])
    .filter((half): half is Chunk => half !== undefined);
}

export const rowCount = (chunk: Chunk): number =>
  chunk.records.length + chunk.responses.length;

/** Halves between taps, or inside the one tap a chunk holds. */
export function splitForIsolation(chunk: Chunk): Chunk[] {
  const byTap = halve(chunk);
  if (byTap.length > 1) return byTap;
  const rows: Chunk[] = [
    ...chunk.records.map((record) => ({ records: [record], responses: [] })),
    ...chunk.responses.map((response) => ({
      records: [],
      responses: [response],
    })),
  ];
  const middle = Math.ceil(rows.length / 2);
  return [rows.slice(0, middle), rows.slice(middle)]
    .filter((half) => half.length > 0)
    .map((half) => ({
      records: half.flatMap((part) => part.records),
      responses: half.flatMap((part) => part.responses),
    }));
}
